import * as PIXI from 'pixi.js';
import type { RawAssetData, TranscriptData } from '../../video-data/types';
import type { BaseAssetSettings } from '../asset/BaseAsset';
import Luminary from '../../index';
import { addAssetToContainer } from '../../libs/buildChildrenForScene';
import { Config } from '../../video-data/config';
import { assertInArray, assertString } from '../../validators';
import { BaseAsset } from '../asset/BaseAsset';
import type { TextAssetStyle } from '../text/TextAssetStyle';
import type { AspectRatio, DestroyOptions } from '../../types';
import type { Transformation } from '../Transformation';
import { BaseGroup } from '../BaseGroup';
import { CanvasCaptionAsset } from '../canvasCaption/asset/CanvasCaptionAsset';
import type { Transcript } from '../text/Transcript';
import { CaptionAssetStyle } from './CaptionAssetStyle';

type CaptionsManagerSettings = BaseAssetSettings & {
    disableCaptions?: boolean;
};

/**
 * Interface though which the style of captions be changed.
 *
 * The style loaded from the scene design is the base styling for captions. This interface is used to update
 * settings after loading the style from the scene design.
 */
export interface CaptionStyle {
    get transformation(): Transformation;
    id: string;
    maxWidth: number;
    maxHeight: number;
    textAlign: string;
    textScaleFactor: number;
    leadingMultiplier: number;
    highlightedTextScale: number;
    textSize: number;
    readingDirection: string;
    // TODO: LU-3182: this is still the old style, we should be updated to the new style
    get normalStyle(): TextAssetStyle;
    get highlightStyle(): TextAssetStyle;
}


type StyleCacheEntry = {
    styleData: unknown;
    decorationsData: RawAssetData[];
};

/**
 * The CaptionsManager displays a scene group's captions using a CanvasCaptionAsset.
 * It manages the caption styles and the decoration workspace.
 * Decorations are additional assets on the caption style design that can be used as a background
 * for the captions.
 */
export class CaptionsManager extends BaseAsset {
    override _classType = 'CaptionsManager';

    // Often this will be the same asset, so need to create fresh copies each time.
    static #styleCache = new Map<string, StyleCacheEntry>();

    // Where any background decorations are, e.g. a gradient behind the captions.
    #decorationsWorkspace: BaseGroup;
    #captionAsset: CanvasCaptionAsset;
    #captionStyle: CaptionAssetStyle = null;
    #shouldLoadDefaultStyleAndDecorations: boolean = false;
    #aspectRatio: AspectRatio = Config.LANDSCAPE_ASPECT_RATIO;
    #sceneDesignId: string = null;
    #disableCaptions: boolean = false;

    constructor(container: any, settings: CaptionsManagerSettings) {
        super(container, settings);
        if (settings.hasOwnProperty('disableCaptions')) {
            this.#disableCaptions = settings.disableCaptions;
        }

        this.#decorationsWorkspace = new BaseGroup(this, {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            isFinalTopLevel: false,
            updateParentOnRedraw: true,
            name: 'decorationsWorkspace',
        });

        this._addAsset(this.#decorationsWorkspace);

        this.#captionAsset = new CanvasCaptionAsset(this, {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            isFinalTopLevel: false,
            updateParentOnRedraw: true,
            name: 'captionWorkspace',
        });
        this._addAsset(this.#captionAsset);
    }

    // Since the CaptionManager doesn't load any external resources such as urls, it should always load.
    // If we use the default implementation then we wouldn't build its children if fetchResources is false.
    override get shouldLoad(): boolean {
        return true;
    }

    get trimmedTranscript(): TranscriptData {
        const startTime = this.asset.startTime;
        const endTime = this.asset.endTime;

        return this.transcript.getTrimmedTranscriptData(startTime, endTime);
    }

    get transcript(): Transcript {
        return this.#captionAsset.transcript;
    }

    get asset(): CanvasCaptionAsset {
        return this.#captionAsset;
    }

    set _aspectRatio(value: AspectRatio) {
        if (this.#aspectRatio === value) {
            return;
        }
        assertInArray(value, Config.ALL_ASPECT_RATIOS);
        this.#aspectRatio = value;
        this.#shouldLoadDefaultStyleAndDecorations = true;
        this.requiresRedraw = true;
    }

    get _aspectRatio() {
        return this.#aspectRatio;
    }

    /**
     * The scene design id to use to style the captions.
     * This scene design must have sceneCategory of 'caption', and have only one text asset at the scene level that will
     * be used as a source of style.
     */
    get sceneDesignId(): string | null {
        return this.#sceneDesignId;
    }

    set sceneDesignId(sceneDesignId: string | null) {
        if (this.#sceneDesignId === sceneDesignId) {
            return;
        }

        assertString(sceneDesignId, true);
        this.#sceneDesignId = sceneDesignId;
        this.#shouldLoadDefaultStyleAndDecorations = true;
        this.requiresRedraw = true;
    }

    /**
     * The text asset used as a source or style to be applied to captions. If null, then a `sceneDesignId`
     * has not yet been set, and the captions will appear as plain text.
     */
    get style(): CaptionStyle {
        return this.#captionStyle;
    }

    #removeDecorations() {
        if (this.#decorationsWorkspace._pixiContainer.children.length === 0) return;
        this._batchRedraw(() => this.#decorationsWorkspace.clear());
    };

    override clear(): void {
        this.#removeDecorations();
        this.sceneDesignId = null;
    }

    /**
     * Sets the visibility of both the captions and decorations PIXI container for rendering purposes. If the
     * visibility is set to false then the caption managers content will not be rendered. If captions are globally
     * disabled through disableCaptions then visibility will be set to false regardless of the value passed in.
     **/
    _setCaptionsAndDecorationsVisibility(visible: boolean) {
        if (this.#disableCaptions) {
            visible = false;
        }
        this.#captionAsset.visible = visible;
        this.#decorationsWorkspace.visible = visible;
    }

    override _checkAndFixProperties() {
        this._setCaptionsAndDecorationsVisibility(this.visible);
    }

    protected override _build(): void {
        if (this.#disableCaptions) {
            return;
        }
        this.#loadCaptionStyleForSceneDesign();
    }

    override get visibleWithUpdate() {
        return super.visibleWithUpdate;
    }

    override set visibleWithUpdate(value: boolean) {
        if (this.visibleWithUpdate === value) {
            return;
        }
        super.visibleWithUpdate = value;
        // rebuild captions when visibility changes because when captions are invisible they aren't built
        this.#captionAsset.requiresRedraw = true;
    }

    override get requiresRedraw() {
        if (!this.isDoneTryingToLoad) {
            return true;
        }
        return super.requiresRedraw;
    }

    override set requiresRedraw(value) {
        super.requiresRedraw = value;
    }

    override get canPlay() {
        return this._childAssets.every(asset => asset.canPlay);
    }

    override get isLoaded() {
        return this._childAssets.every(asset => asset.isLoaded);
    }

    override get isDoneTryingToLoad() {
        return this._childAssets.every(asset => asset.isDoneTryingToLoad);
    }

    get #styleCacheKey() {
        if (!this.sceneDesignId) {
            throw new Error('A style cache key requires a valid sceneDesignId');
        }
        // this.root might not be set - perhaps in tests. It doesn't matter for the key.
        return `${this.sceneDesignId}_${this._aspectRatio}_${this.root?.textDirection ?? ''}`;
    }

    /**
     * Returns the max text bounds of the caption asset if a transcript is set, otherwise returns
     * an empty rectangle.
     */
    override get approximateMaxBounds() {
        if (this.transcript.empty) {
            return PIXI.Rectangle.EMPTY;
        }
        return this.asset.approximateMaxBounds;
    }

    /**
     * @internal
     */
    _propagateTextDirection(textDirection: string) {
        if (!this.style) {
            return;
        }

        this.style.readingDirection = textDirection;
    }


    _styleCustomSave() {
        return this.#captionStyle?.save() ?? null;
    }

    _styleCustomLoad(style: unknown) {
        this.#shouldLoadDefaultStyleAndDecorations = false;
        if (!style) {
            this.#destroyStyle();
            return;
        }

        if (!this.#captionStyle) {
            this.#captionStyle = new CaptionAssetStyle({
                parent: this,
                redrawParentOnUpdate: true,
                autoUpdate: true,
                fetchResources: false,
            });
        }
        this.#captionStyle.load(style);
        this._assetApplyStyle(style);
    }

    _assetApplyStyle(style) {
        if (!style) {
            return;
        }
        this.#captionAsset.applyTextAssetStyle(style);
    }

    _decorationsCustomSave() {
        return this.#decorationsWorkspace.getAssets({ deep: false }).map(child => child.save());
    }

    _decorationsCustomLoad(decorations: RawAssetData[]) {
        decorations.forEach(decoration => {
            const asset = addAssetToContainer(this.#decorationsWorkspace, decoration.classType, {
                id: decoration.id,
                fetchResources: this.#disableCaptions ? false : this.fetchResources,
            });
            asset.load(decoration);
        });
    }

    _assetCustomLoad(assetData: RawAssetData) {
        this.#captionAsset.load(assetData);
    }

    _assetCustomSave() {
        return this.#captionAsset.save();
    }

    #destroyStyle() {
        if (!this.#captionStyle) {
            return;
        }
        this.#captionStyle.destroy();
        this.#captionStyle = null;
    }

    override get readonlyProperties() {
        return [];
    }

    override get propertiesToSerialize(): string[] {
        return [
            // our users can hide the captions, so we need to serialize the visibility
            'visible',
            'sceneDesignId',
            'style',
            'asset',
            'decorations',
        ];
    }

    override destroy(options?: DestroyOptions) {
        super.destroy(options);
        this.#destroyStyle();
    }

    reloadStyle() {
        this.#shouldLoadDefaultStyleAndDecorations = true;
        this.requiresRedraw = true;
    }

    /**
     * @throws If there is no scene in the template for the given scene design id.
     * @throws If the scene design for the given scene design id is not of the "caption" category.
     * @throws If there is no text asset at the scene level to use for styling.
     */
    #loadCaptionStyleForSceneDesign() {
        if (!this.root.isTemplateBundleSet) {
            // We cannot load the style from the template if we don't have the template bundle.
            // This is fine and expected when loading a video, since the previously set style is serialized and loaded again.
            return;
        }

        if (!this.#shouldLoadDefaultStyleAndDecorations) {
            return;
        }
        this.#shouldLoadDefaultStyleAndDecorations = false;

        this.#removeDecorations();
        if (!this.sceneDesignId) {
            this.#destroyStyle();
            return;
        }

        const styleKey = this.#styleCacheKey;
        const cachedStyle = CaptionsManager.#styleCache.get(styleKey);
        if (cachedStyle) {
            this._styleCustomLoad(cachedStyle.styleData);
            this._decorationsCustomLoad(cachedStyle.decorationsData);
            return;
        }

        const dataLuminary = new Luminary({
            disableRenderer: true,
            fetchResources: false,
        });
        dataLuminary.textDirection = this.root.textDirection;
        dataLuminary.aspectRatio = this.root.aspectRatio;
        dataLuminary.resolutionValue = this.root.resolutionValue;
        dataLuminary.template.load(this.root.template.save());
        dataLuminary.template.watermark.visible = false;
        try {
            const sceneDesignConfig = this.root._getSceneDesignConfig(this.sceneDesignId, this._aspectRatio);
            if (!sceneDesignConfig) {
                throw new Error(`Expecting scene design "${this.sceneDesignId}" for "${this._aspectRatio}" to be in the current template`);
            }
            const scene = dataLuminary.addScene(({ data: sceneDesignConfig }));
            if (scene.sceneCategory !== Config.CATEGORY_CAPTION) {
                throw new Error(`Expecting scene design "${this.sceneDesignId}" for "${this._aspectRatio}" to be in the "caption" category`);
            }
            const textAssets = scene.getAssetsByType(Config.TEXT_ASSET_TYPE, { deep: false });
            if (!textAssets || textAssets.length !== 1) {
                throw new Error(`Expecting one text asset on the scene to use as a caption in scene design "${this.sceneDesignId}" for "${this._aspectRatio}"`);
            }

            const styleData = textAssets[0].save();
            this._styleCustomLoad(styleData);

            const decorationsData = scene.getAssets({ deep: false })
                .filter(child => !Config.isTextAsset(child))
                .map(decoration => decoration.save()) as RawAssetData[];
            this._decorationsCustomLoad(decorationsData);

            CaptionsManager.#styleCache.set(styleKey, { styleData, decorationsData });
        }
        finally {
            void dataLuminary.destroy();
        }
    }
}
